import type { CaptureErrorCode, QrCapture } from '../types'
import { getCenteredContinuousScanRegion } from './midniQrImage'
import type { Point } from './midniQrImage'
import { decodeMiDniQrFromVideoFrame } from './midniQrPayload'
import { toMiDniExtractedData } from './midniQrData'

/**
 * Lectura continua del QR de MiDNI. Copiado de MiDniQrCapture.tsx de la app actual: qr-scanner 1.4.2
 * cargado con import(), worker forzado, cámara trasera, 12 lecturas/s, región centrada de 2400 px,
 * ajuste de resolución y enfoque, y la misma clasificación de errores de cámara.
 */
const MIDNI_SCAN_MAX_DIMENSION = 2400
const MIDNI_CAMERA_IDEAL_WIDTH = 1920
const MIDNI_CAMERA_IDEAL_HEIGHT = 1080

export function cameraErrorCode(error: unknown, secure = window.isSecureContext): CaptureErrorCode {
  if (secure === false) return 'insecure'
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return 'no_camera'
    if (['NotFoundError', 'NotReadableError', 'OverconstrainedError', 'AbortError'].includes(error.name)) return 'camera_unavailable'
  }
  return 'camera'
}

async function optimizeCameraForQrScanning(video: HTMLVideoElement) {
  const stream = video.srcObject
  if (!stream || typeof (stream as { getVideoTracks?: unknown }).getVideoTracks !== 'function') return
  const track = (stream as MediaStream).getVideoTracks()[0]
  if (!track?.getCapabilities) return
  const capabilities = track.getCapabilities() as { width?: { max?: number }; height?: { max?: number }; focusMode?: string[] }
  const constraints: { width: { ideal: number }; height: { ideal: number }; advanced?: Array<{ focusMode: string }> } = {
    width: { ideal: Math.min(MIDNI_CAMERA_IDEAL_WIDTH, capabilities.width?.max ?? MIDNI_CAMERA_IDEAL_WIDTH) },
    height: { ideal: Math.min(MIDNI_CAMERA_IDEAL_HEIGHT, capabilities.height?.max ?? MIDNI_CAMERA_IDEAL_HEIGHT) },
  }
  if (capabilities.focusMode?.includes('continuous')) constraints.advanced = [{ focusMode: 'continuous' }]
  try {
    await track.applyConstraints(constraints as unknown as MediaTrackConstraints)
  } catch {
    // Las restricciones dependen del dispositivo; el flujo sigue con los valores por defecto.
  }
}

export const midniQrCapture: QrCapture = {
  async start(video, reserveId, onDetected, onError) {
    let active = true
    let parsing = false
    let scanner: { stop: () => void; destroy: () => void } | undefined
    const stop = () => {
      active = false
      scanner?.stop()
      scanner?.destroy()
      scanner = undefined
      const stream = video.srcObject
      if (stream && typeof (stream as MediaStream).getTracks === 'function') (stream as MediaStream).getTracks().forEach((t) => t.stop())
      video.srcObject = null
    }
    if (window.isSecureContext === false) {
      onError('insecure')
      return stop
    }
    try {
      const { default: QrScanner } = await import('qr-scanner')
      if (!active) return stop
      ;(QrScanner as unknown as { _disableBarcodeDetector?: boolean })._disableBarcodeDetector = true
      const instance = new QrScanner(
        video,
        (result: { cornerPoints: Point[] }) => {
          if (!active || parsing) return
          parsing = true
          decodeMiDniQrFromVideoFrame(video, result.cornerPoints)
            .then((local) => {
              if (!local || !active) return
              stop()
              onDetected(toMiDniExtractedData(local, reserveId))
            })
            .catch(() => undefined) // Si no se puede parsear, sigue buscando (como hoy).
            .finally(() => {
              parsing = false
            })
        },
        {
          preferredCamera: 'environment',
          maxScansPerSecond: 12,
          returnDetailedScanResult: true,
          calculateScanRegion: (v: HTMLVideoElement) => getCenteredContinuousScanRegion(v.videoWidth, v.videoHeight, MIDNI_SCAN_MAX_DIMENSION),
          onDecodeError: () => undefined,
        },
      )
      scanner = instance
      await instance.start()
      await optimizeCameraForQrScanning(video)
      if (!active) stop()
    } catch (error) {
      if (active) onError(cameraErrorCode(error))
    }
    return stop
  },
}
